import { idempotencyKey } from "./api.js";
import {
  paymentOperationAttempt,
  type PaymentOperationAttempt,
} from "./paymentOperationModel.js";
import type {
  LessonBlockProduct,
  LessonBlockSource,
  OnSitePaymentMethod,
  ProviderService,
} from "./staff.js";

const LESSON_BLOCK_EXPIRY_DAYS = 180;
const FUTURE_PAYMENT_GRACE_MS = 5 * 60 * 1000;

export type AutomaticSettlementPolicy =
  | "settle_outstanding_first"
  | "no_automatic_backdating";

export function automaticSettlementConfirmation(
  policy: AutomaticSettlementPolicy,
  quantity: number,
): string {
  return policy === "settle_outstanding_first"
    ? "Previous eligible lessons with no recorded payment will be considered first, oldest first; any credits left remain available for future lessons."
    : `All ${quantity} credits will remain available and will not automatically be applied to previous lessons.`;
}

export function lessonBlockPaidAtProblem(
  paidAtIso: string,
  now: Date = new Date(),
): string | null {
  const paidAt = new Date(paidAtIso);
  if (Number.isNaN(paidAt.getTime())) return "Enter a valid payment date and time.";
  if (paidAt.getTime() > now.getTime() + FUTURE_PAYMENT_GRACE_MS) {
    return "The payment date can't be in the future";
  }
  const oldestAllowed = new Date(now);
  oldestAllowed.setUTCDate(oldestAllowed.getUTCDate() - LESSON_BLOCK_EXPIRY_DAYS);
  if (paidAt.getTime() <= oldestAllowed.getTime()) {
    return (
      `The payment date is more than ${LESSON_BLOCK_EXPIRY_DAYS} days ago, ` +
      "so the block would already have expired"
    );
  }
  return null;
}

export function lessonBlockExpiryIso(paidAtIso: string): string | null {
  const paidAt = new Date(paidAtIso);
  if (Number.isNaN(paidAt.getTime())) return null;
  const expiry = new Date(paidAt);
  expiry.setUTCDate(expiry.getUTCDate() + LESSON_BLOCK_EXPIRY_DAYS);
  return expiry.toISOString();
}

export interface LessonBlockRegistrationInput {
  portal_student_id: string;
  student_name: string;
  provider_id: number;
  block_service_id: number;
  payment_amount: string;
  payment_method: OnSitePaymentMethod;
  paid_at?: string;
  notes: string;
  automatic_settlement_policy: AutomaticSettlementPolicy;
}

/**
 * Keep compatibility in one place for both the administrative sale form and
 * the ordinary tutor shortcut. The provider-service catalogue remains the
 * authority for which lesson services belong to the selected provider; this
 * helper only intersects that answer with configured lesson-block products.
 */
export function compatibleLessonBlockProducts(
  products: readonly LessonBlockProduct[],
  services: readonly ProviderService[],
  creditQuantity?: number,
): LessonBlockProduct[] {
  const serviceIds = new Set(services.map((service) => service.id));
  return products.filter(
    (product) =>
      serviceIds.has(product.amelia_lesson_service_id) &&
      (creditQuantity === undefined || product.credit_quantity === creditQuantity),
  );
}

/**
 * Use the selected tutor's Amelia block-sale service as the source of truth for
 * the amount received. This preserves provider-specific pricing and configured
 * block discounts instead of deriving a total from the ordinary lesson price.
 */
export function lessonBlockDefaultPaymentAmount(
  product: LessonBlockProduct | undefined,
  services: readonly ProviderService[],
): string {
  if (!product) return "";
  const price = services.find(
    (service) => service.id === product.amelia_block_service_id,
  )?.price;
  return price === null || price === undefined ? "" : String(price);
}

export function normalizePaymentAmount(value: string): string | null {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const [whole = "0", fraction = ""] = normalized.split(".");
  const amount = `${whole}.${fraction.padEnd(2, "0")}`;
  return Number(amount) > 0 ? amount : null;
}

export function lessonBlockRegistrationInput({
  studentId,
  studentName,
  providerId,
  product,
  paymentAmount,
  paymentMethod,
  paidAt,
  notes = "",
  automaticSettlementPolicy = "settle_outstanding_first",
}: {
  studentId: string;
  studentName: string;
  providerId: number;
  product: LessonBlockProduct;
  paymentAmount: string;
  paymentMethod: OnSitePaymentMethod;
  paidAt?: string;
  notes?: string;
  automaticSettlementPolicy?: AutomaticSettlementPolicy;
}): LessonBlockRegistrationInput {
  return {
    portal_student_id: studentId,
    student_name: studentName,
    provider_id: providerId,
    block_service_id: product.amelia_block_service_id,
    payment_amount: paymentAmount,
    payment_method: paymentMethod,
    ...(paidAt === undefined ? {} : { paid_at: paidAt }),
    notes,
    automatic_settlement_policy: automaticSettlementPolicy,
  };
}

/** One logical block addition keeps one idempotency key across ambiguous retry. */
export function lessonBlockSaleAttempt(
  previous: PaymentOperationAttempt | null,
  input: LessonBlockRegistrationInput,
  createKey: () => string = idempotencyKey,
): PaymentOperationAttempt {
  return paymentOperationAttempt(previous, "lesson_block_sale", input, createKey);
}

/** Human source wording for the ordinary tutor surface. */
export function tutorLessonBlockSourceLabel(source: LessonBlockSource): string {
  if (source === "paid_cancellation") return "Paid cancellation credit";
  return source === "amelia" ? "Imported from Amelia" : "Added in Hub";
}
